import _ from "lodash";
import {
    ContaminationDeclarationSubstanceAttributesInput,
    ContaminationDeclarationUpdateAttributesInput,
} from "../../codegen/graphql";
import {
    CONTAMINATION_OTHER_FIELD,
    ContaminationDeclarationFormValues,
    DECLARATION_ACCEPTED_FIELD,
    RUNNING_TIME_HOURS_FIELD,
    SUBSTANCES_ADDITIONAL_INFORMATION_FIELD,
    SUBSTANCES_FIELD,
    TEXT_FIELDS,
    YES_NO_FIELDS,
} from "./fields";
import { SubstanceRow } from "./types";

export type UpdateInputErrorCode = 'REQUIRED' | 'INVALID_NUMBER';

export interface BuildUpdateInputResult {
    /** Only the answers the user changed — an omitted key leaves the stored answer untouched. */
    input: ContaminationDeclarationUpdateAttributesInput;
    /** Client-side rejections keyed by field; when non-empty nothing should be sent. */
    errors: Partial<Record<string, UpdateInputErrorCode>>;
    hasChanges: boolean;
}

// Thousands separators and stray spacing the user may type ("8 140", "8.140", "8,140").
const RUNNING_TIME_SEPARATORS = /[\s.,'_  ]/g;

/**
 * `runningTimeHours` is an `Int` on the wire and the backend does not
 * normalize, so separators are stripped here. Empty means "no answer" (null).
 */
export const parseRunningTimeHours = (
    raw: string | null | undefined,
): { value: number | null; error?: UpdateInputErrorCode } => {
    const stripped = (raw ?? '').replace(RUNNING_TIME_SEPARATORS, '');
    if (stripped === '') return { value: null };
    if (!/^\d+$/.test(stripped)) return { value: null, error: 'INVALID_NUMBER' };
    return { value: parseInt(stripped, 10) };
};

/** Trims every cell and drops rows that are entirely empty (the backend rejects those). */
/**
 * Whether any answer other than the consent box differs from the saved state.
 * Drives the re-confirmation rule: editing a declaration that was already
 * accepted unticks the consent, so the editor confirms the changed content.
 */
export const haveAnswersChanged = (
    values: ContaminationDeclarationFormValues,
    initial: ContaminationDeclarationFormValues,
): boolean =>
    (Object.keys(values) as Array<keyof ContaminationDeclarationFormValues>).some((key) => {
        if (key === DECLARATION_ACCEPTED_FIELD) return false;
        if (key === SUBSTANCES_FIELD) {
            return !_.isEqual(normalizeSubstanceRows(values.substances), normalizeSubstanceRows(initial.substances));
        }
        const a = values[key];
        const b = initial[key];
        return (typeof a === 'string' ? a.trim() : a) !== (typeof b === 'string' ? b.trim() : b);
    });

export const normalizeSubstanceRows = (rows: SubstanceRow[] | null | undefined): SubstanceRow[] =>
    (rows ?? [])
        .map((row) => ({
            chemical: (row.chemical ?? '').trim(),
            dangerClass: (row.dangerClass ?? '').trim(),
            risks: (row.risks ?? '').trim(),
            firstAid: (row.firstAid ?? '').trim(),
        }))
        .filter((row) => Object.values(row).some((cell) => cell !== ''));

const toSubstanceInput = (row: SubstanceRow): ContaminationDeclarationSubstanceAttributesInput => ({
    chemical: row.chemical || null,
    dangerClass: row.dangerClass || null,
    risks: row.risks || null,
    firstAid: row.firstAid || null,
});

/**
 * Diffs the current form values against the values the form was loaded with
 * and builds the partial update input the backend expects:
 * - untouched answers are omitted (a partial save must stay partial),
 * - yes/no answers are only ever sent as `true`/`false`, never `null`,
 * - a cleared optional text is sent as `null` (that is the "clear" act),
 * - the substances table is sent as a whole only when a row changed,
 * - ticking "other harmful substances" ships the additional information in the
 *   same submission, mirroring the server rule that rejects it otherwise.
 */
export const buildContaminationDeclarationUpdateInput = (
    values: ContaminationDeclarationFormValues,
    initial: ContaminationDeclarationFormValues,
): BuildUpdateInputResult => {
    const input: ContaminationDeclarationUpdateAttributesInput = {};
    const errors: Partial<Record<string, UpdateInputErrorCode>> = {};

    // The two loops below write by index, and `src/codegen/graphql.ts` is a
    // checked-in artifact of the federation gateway - so a field this app already
    // asks for is not on the generated input type until the backend has shipped and
    // CI has published the subgraph to Cosmo. `YES_NO_FIELDS` is the app's own
    // inventory and is the honest source here; this view keeps the gap from turning
    // into a compile error on a field the schema will carry. Drop it once the
    // generated type is regenerated and indexing by `YesNoField` type-checks again.
    const byIndex = input as Record<string, unknown>;

    TEXT_FIELDS.forEach((field) => {
        const next = (values[field] ?? '').trim();
        const prev = (initial[field] ?? '').trim();
        if (next !== prev) byIndex[field] = next === '' ? null : next;
    });

    YES_NO_FIELDS.forEach((field) => {
        const next = values[field];
        if (typeof next === 'boolean' && next !== initial[field]) byIndex[field] = next;
    });

    const nextRunningTime = parseRunningTimeHours(values.runningTimeHours);
    const prevRunningTime = parseRunningTimeHours(initial.runningTimeHours);
    if (nextRunningTime.error) {
        errors[RUNNING_TIME_HOURS_FIELD] = nextRunningTime.error;
    } else if (nextRunningTime.value !== prevRunningTime.value) {
        input.runningTimeHours = nextRunningTime.value;
    }

    if (values.declarationAccepted !== initial.declarationAccepted) {
        input.declarationAccepted = values.declarationAccepted;
    }

    const nextRows = normalizeSubstanceRows(values.substances);
    const prevRows = normalizeSubstanceRows(initial.substances);
    if (!_.isEqual(nextRows, prevRows)) {
        input.substances = nextRows.map(toSubstanceInput);
    }

    if (byIndex[CONTAMINATION_OTHER_FIELD] === true) {
        const info = (values[SUBSTANCES_ADDITIONAL_INFORMATION_FIELD] ?? '').trim();
        if (info === '') {
            errors[SUBSTANCES_ADDITIONAL_INFORMATION_FIELD] = 'REQUIRED';
        } else {
            input[SUBSTANCES_ADDITIONAL_INFORMATION_FIELD] = info;
        }
    }

    return { input, errors, hasChanges: Object.keys(input).length > 0 };
};
